import classNames from 'classnames';

import React from 'react';

import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useForm, SubmitHandler } from 'react-hook-form';

import Button from 'reactstrap/lib/Button';
import Card from 'reactstrap/lib/Card';
import CardBody from 'reactstrap/lib/CardBody';
import CardFooter from 'reactstrap/lib/CardFooter';
import CardText from 'reactstrap/lib/CardText';
import Form from 'reactstrap/lib/Form';
import FormFeedback from 'reactstrap/lib/FormFeedback';
import FormGroup from 'reactstrap/lib/FormGroup';
import Input from 'reactstrap/lib/Input';
import InputGroup from 'reactstrap/lib/InputGroup';
import InputGroupAddon from 'reactstrap/lib/InputGroupAddon';
import InputGroupText from 'reactstrap/lib/InputGroupText';
import Label from 'reactstrap/lib/Label';
import Modal from 'reactstrap/lib/Modal';
import ModalBody from 'reactstrap/lib/ModalBody';
import ModalFooter from 'reactstrap/lib/ModalFooter';
import ModalHeader from 'reactstrap/lib/ModalHeader';
import Spinner from 'reactstrap/lib/Spinner';

import { upgradeToCustomer } from '@ttstr/api';
import { useToggle } from '@ttstr/utils';
import { useAuth } from '../Auth/AuthContext';

interface Passwords {
  password: string;
  passwordRepeat: string;
}

interface OwnProps {
  orderId: number;
  xtoken: string;
  email: string;
  color?: string;
  closeIcon?: React.ReactNode;
}

type Props = Readonly<OwnProps>;

const CustomerFromOrder: React.FC<Props> = ({ orderId, xtoken, email, color = 'info', closeIcon }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [signupError, setSignupError] = React.useState<string>(null);
  const [isOpen, toggleModal] = useToggle(false);
  const { loggedIn, initialized } = useAuth();
  const formContext = useForm<Passwords>({
    mode: 'onChange',
  });
  const { register, errors, handleSubmit, formState, getValues } = formContext;
  const { isSubmitting, isValid } = formState;

  const close = closeIcon ? (
    <button className="close" onClick={toggleModal}>
      {closeIcon}
    </button>
  ) : null;

  const onSubmit: SubmitHandler<Passwords> = async (values) => {
    setSignupError(null);
    const { password } = values;

    try {
      await upgradeToCustomer(orderId, xtoken, password);
      toggleModal();
      history.push('/customer/registered');
    } catch (error) {
      // TODO doesn't work in test
      if (error.details) setSignupError('FAILED_2');
      else setSignupError('FAILED');
    }
  };

  if (!initialized || loggedIn) return null;

  return (
    <>
      <Card className="mb-3" color={color}>
        <CardBody>
          <CardText>{t(`CUSTOMER.NOACCOUNT`)}</CardText>
        </CardBody>
        <CardFooter>
          <Button type="button" color="primary" block onClick={toggleModal}>
            {t(`CUSTOMER.NOACCOUNTBUTTON`)}
          </Button>
        </CardFooter>
      </Card>
      <Modal
        size="sm"
        fade
        backdrop="static"
        isOpen={isOpen}
        toggle={toggleModal}
        autoFocus={false}
        returnFocusAfterClose={false}
      >
        <ModalHeader toggle={toggleModal} close={close}>
          {t(`CUSTOMER.REGISTER`)}
        </ModalHeader>
        <Form action="#" onSubmit={handleSubmit(onSubmit)}>
          <fieldset disabled={isSubmitting}>
            <ModalBody>
              <FormGroup tag="fieldset">
                <Label for="username" className="sr-only">
                  {t(`LOGIN.USERNAME`)}
                </Label>
                <InputGroup>
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="fal fa-user" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input type="email" id="username" defaultValue={email} disabled />
                </InputGroup>
              </FormGroup>
              <FormGroup tag="fieldset">
                <Label for="password" className="sr-only">
                  {t(`CUSTOMER.PASSWORD`)}
                </Label>
                <InputGroup className={classNames({ 'is-invalid': Boolean(errors.password) })}>
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="fal fa-key" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    type="password"
                    id="password"
                    name="password"
                    placeholder={t(`CUSTOMER.PASSWORD`)}
                    autoComplete="new-password"
                    className={classNames({ 'is-invalid': Boolean(errors.password) })}
                    innerRef={register({
                      required: true,
                      minLength: 6,
                    })}
                  />
                </InputGroup>
                {errors.password && (
                  <FormFeedback>{t(`FORM.VALIDATION.${errors.password.type.toUpperCase()}`)}</FormFeedback>
                )}
              </FormGroup>
              <FormGroup tag="fieldset">
                <Label for="passwordRepeat" className="sr-only">
                  {t(`CUSTOMER.PASSWORD_REPEAT`)}
                </Label>
                <InputGroup className={classNames({ 'is-invalid': Boolean(errors.passwordRepeat) })}>
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="fal fa-key" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    type="password"
                    id="passwordRepeat"
                    name="passwordRepeat"
                    placeholder={t(`CUSTOMER.PASSWORD_REPEAT`)}
                    autoComplete="new-password"
                    className={classNames({ 'is-invalid': Boolean(errors.passwordRepeat) })}
                    innerRef={register({
                      required: true,
                      minLength: 6,
                      validate: {
                        'CUSTOM.PASSWORDS_DONT_MATCH': (value) => String(value) === String(getValues().password),
                      },
                    })}
                  />
                </InputGroup>
                {errors.passwordRepeat && (
                  <FormFeedback>{t(`FORM.VALIDATION.${errors.passwordRepeat.type.toUpperCase()}`)}</FormFeedback>
                )}
              </FormGroup>
              {signupError === 'FAILED_2' && <p className="text-danger">{t('CUSTOMER.SIGNUP.FAILED_2')}</p>}
              {signupError === 'FAILED' && <p className="text-danger">{t('CUSTOMER.SIGNUP.FAILED')}</p>}
            </ModalBody>
            <ModalFooter className="flex-row-reverse justify-content-between">
              <Button color="primary" type="submit" disabled={!isValid}>
                {t(`CUSTOMER.REGISTER`)}
                <span className={classNames('spinner-wrapper', { invisible: !isSubmitting })}>
                  <Spinner color="primary" size="sm" />
                </span>
              </Button>
              <Button onClick={toggleModal} color="link" type="button">
                {t(`LOGIN.CANCEL`)}
              </Button>
            </ModalFooter>
          </fieldset>
        </Form>
      </Modal>
    </>
  );
};

export default React.memo(CustomerFromOrder);
